import React, { useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useInstalledAppsWithStatus, type InstalledAppWithStatus } from '@/hooks/useInstalledAppsWithStatus';

interface InstalledAppsReviewModalProps {
  open: boolean;
  onDone: (allowedAppIds: string[]) => void;
  onCancel: () => void;
}

const InstalledAppsReviewModal: React.FC<InstalledAppsReviewModalProps> = ({
  open,
  onDone,
  onCancel,
}) => {
  const { apps, isLoading, error } = useInstalledAppsWithStatus(open);
  const [toggles, setToggles] = useState<Record<string, boolean>>({});
  const [initialized, setInitialized] = useState(false);

  // Initialize toggles from fetched app statuses
  React.useEffect(() => {
    if (apps.length > 0 && !initialized) {
      const initial: Record<string, boolean> = {};
      apps.forEach((app) => {
        const key = app.trackId || app.bundleId;
        initial[key] = app.status === 'allowed';
      });
      setToggles(initial);
      setInitialized(true);
    }
  }, [apps, initialized]);

  // Reset when modal closes
  React.useEffect(() => {
    if (!open) {
      setInitialized(false);
      setToggles({});
    }
  }, [open]);

  const handleToggle = (app: InstalledAppWithStatus) => {
    const key = app.trackId || app.bundleId;
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allowedCount = useMemo(
    () => Object.values(toggles).filter(Boolean).length,
    [toggles]
  );

  const handleDone = () => {
    const allowedAppIds: string[] = [];
    apps.forEach((app) => {
      const key = app.trackId || app.bundleId;
      if (toggles[key] && app._id) {
        allowedAppIds.push(app._id);
      }
    });
    onDone(allowedAppIds);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-md mx-auto max-h-[85dvh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="text-lg font-bold text-foreground">
            Review Installed Apps
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            All apps are currently allowed. Turn off any apps you want to block.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="px-6 py-12 text-center text-sm text-destructive">
            {error}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 -webkit-overflow-scrolling-touch" style={{ maxHeight: 'calc(85dvh - 180px)' }}>
            <div className="space-y-1 px-4 pb-4">
              {apps.map((app) => {
                const key = app.trackId || app.bundleId;
                const isAllowed = toggles[key] ?? app.status === 'allowed';

                return (
                  <div
                    key={key}
                    className="flex items-center gap-3 py-3 border-b border-border/30 last:border-0"
                  >
                    {/* App icon */}
                    {app.iconUrl ? (
                      <img
                        src={app.iconUrl}
                        alt={app.name || app.bundleId}
                        className="w-10 h-10 rounded-xl flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-muted-foreground">
                          {(app.name || app.bundleId).charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}

                    {/* App name + badge */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {app.name || app.bundleId}
                      </p>
                      {app.status === 'allowed' && (
                        <span className="text-[10px] text-muted-foreground font-medium">
                          In Library
                        </span>
                      )}
                    </div>

                    {/* Toggle */}
                    <Switch
                      checked={isAllowed}
                      onCheckedChange={() => handleToggle(app)}
                      colorWhenOn="success"
                      colorWhenOff="destructive"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="px-6 py-4 border-t border-border/50 flex-row items-center justify-between sm:justify-between gap-3">
          <span className="text-sm text-muted-foreground font-medium">
            {allowedCount} allowed
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} size="sm">
              Cancel
            </Button>
            <Button onClick={handleDone} size="sm" disabled={isLoading}>
              Done
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InstalledAppsReviewModal;
